import { createElement } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from './list/DataTableColumnHeader';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import { resolveEnumItems } from '@resource-kit/controllers';
import { toFilterType } from '@resource-kit/controllers';
import { viewFieldOf } from '@resource-kit/controllers/react';
import { resolveModifier } from '@resource-kit/controllers';
import { resolveFieldWidget } from '@resource-kit/controllers';
import type { AnyFieldDef, ModelField } from '@resource-kit/model';
import type { FieldModifierCtx } from '@resource-kit/model';

export interface CreateColumnsOptions<T> {
  mode: 'list' | 'detail';
  modifierCtx?: FieldModifierCtx<T> | null;
}

/**
 * Re-export shim: `viewFieldOf` moved into `@resource-kit/controllers`'
 * `react/form/viewField` with #2167 PR-3. `createColumns` below did NOT — it
 * builds TanStack `ColumnDef`s out of host UI components and follows in #2169 —
 * so this file keeps both halves reachable from the path its importers use.
 */
export { viewFieldOf } from '@resource-kit/controllers/react';

/** Build TanStack columns directly from the v2 resource contracts. */
export function createColumns<T, F extends Record<string, AnyFieldDef>>(
  bundle: ResourceBundle<T, F>,
  opts: CreateColumnsOptions<T>,
): ColumnDef<T>[] {
  const modifierCtx = opts.modifierCtx ?? null;
  const viewModifierCtx =
    modifierCtx?.record == null ? null : { ...modifierCtx, record: modifierCtx.record };

  return bundle.list.items.map((item) => {
    const modelField = bundle.model.fields[item.field];
    const field = viewFieldOf(item, modelField);
    const widgetField: ModelField = {
      ...modelField,
      label: item.label ?? modelField.label,
    };
    const widget = resolveFieldWidget({
      itemWidget: item.widget,
      fieldWidget: modelField.widget,
      type: modelField.type,
    });
    const label = item.label ?? modelField.label;

    return {
      accessorKey: item.field,
      enableSorting: bundle.host.sortable ? bundle.host.sortable.includes(item.field) : true,
      header: ({ column }) => createElement(DataTableColumnHeader<T>, { column, title: label }),
      meta: {
        label,
        filter: {
          type: toFilterType(field),
          label,
          fieldId: modelField.filterId ?? item.field,
          enumValues: resolveEnumItems(field),
        },
        // One modifier per surface. On the LIST that means "off by
        // default in the column picker"; on the DETAIL it means "never
        // rendered", because that surface has no picker to reverse it.
        hidden:
          opts.mode === 'list'
            ? resolveModifier(item.columnInvisible, viewModifierCtx)
            : resolveModifier(item.detailInvisible, viewModifierCtx),
        field,
      },
      cell: ({ getValue, row }) =>
        createElement(widget.display, {
          value: getValue(),
          record: row.original,
          field: widgetField,
          mode: 'display',
          surface: opts.mode,
        }),
    } satisfies ColumnDef<T>;
  });
}
